import React, { useCallback, useEffect, useState } from "react";
import useApi from "@/hooks/useApi";
import CustomSelect from "./../CustomSelect";
import {
  ChevronSelectorVertical,
  ChevronLeft,
  ChevronRight,
  ChevronLeftDouble,
  ChevronRightDouble,
} from "@velaro/velaro-component-lib/";

interface IntegrationLog {
  id: number;
  integration: string;
  message: string;
  additionalInfo: string;
  createdAt: number;
}

export default function NetsuiteLog() {
  const api = useApi();
  const [logs, setLogs] = useState<IntegrationLog[]>([]);
  const [selectedLogs, setSelectedLogs] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const getLogs = useCallback(async () => {
    const response = await api.messaging.get("Integration/Logs/Netsuite");
    const json = (await response.json()) as IntegrationLog[];
    setLogs(json);
  }, [api.messaging]);

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [sortBy, setSortBy] = useState<keyof IntegrationLog>("createdAt");

  useEffect(() => {
    getLogs();
  }, [getLogs]);

  const totalPages = Math.ceil(logs.length / pageSize);

  // Sorting logic with dynamic type handling
  const sortedLogs = [...logs].sort((a, b) => {
    const valA = a[sortBy];
    const valB = b[sortBy];

    if (typeof valA === "string" && typeof valB === "string") {
      return sortOrder === "asc"
        ? valA.localeCompare(valB)
        : valB.localeCompare(valA);
    }

    return sortOrder === "asc" ? (valA as number) - (valB as number) : (valB as number) - (valA as number);
  });

  const displayedLogs = sortedLogs.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  const handleSort = (column: keyof IntegrationLog) => {
    if (sortBy === column) {
      setSortOrder((prevOrder) => (prevOrder === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSelectRow = (id: number) => {
    setSelectedLogs((prev) =>
      prev.includes(id) ? prev.filter((logId) => logId !== id) : [...prev, id]
    );
  };

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1);
  };

  return (
    <div className="w-full border rounded-2xl shadow-md">
      <table className="w-full table-auto">
        <thead>
          <tr>
            <th className="px-4 pb-2 pt-4 border-b text-left text-gray-700 w-[50px]">
              <input
                type="checkbox"
                onChange={(e) =>
                  setSelectedLogs(
                    e.target.checked ? displayedLogs.map((log) => log.id) : []
                  )
                }
                checked={
                  selectedLogs.length > 0 &&
                  displayedLogs.every((log) => selectedLogs.includes(log.id))
                }
              />
            </th>
            <th
              className="p-2 pt-4 border-b text-left text-gray-700 w-[200px] flex justify-between items-center cursor-pointer w-full"
              onClick={() => handleSort("createdAt")}
            >
              Timestamp
              {React.createElement(ChevronSelectorVertical, {
                className: "text-slate-800 text-sm font-medium h-4",
              })}
            </th>
            <th
              className="p-2 pt-4 border-b text-left text-gray-700 cursor-pointer"
              onClick={() => handleSort("message")}
            >
              Message
            </th>
          </tr>
        </thead>
        <tbody>
          {displayedLogs.map((log) => (
            <tr key={log.id}>
              <td className="px-4 py-2 border-b  w-[50px]">
                <input
                  type="checkbox"
                  checked={selectedLogs.includes(log.id)}
                  onChange={() => handleSelectRow(log.id)}
                />
              </td>
              <td className="p-2 border-b w-[200px] whitespace-nowrap">
                {new Date(log.createdAt)
                  .toLocaleString("en-US", {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                    hour12: false,
                  })
                  .replace(",", "")}
              </td>
              <td className="p-2 pr-4 border-b break-all whitespace-pre-wrap w-full">{log.message}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="flex items-center justify-between p-4">
        <span className="text-slate-500 text-sm font-normal">
          Page {currentPage} of {totalPages}
        </span>

        <div className="flex items-center space-x-2">
          <button
            className="p-2 rounded-md bg-white hover:bg-gray-300 disabled:opacity-50"
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
          >
            {React.createElement(ChevronLeftDouble, { className: "text-slate-500" })}
          </button>
          <button
            className="p-2 rounded-md bg-white hover:bg-gray-300 disabled:opacity-50"
            onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
          >
            {React.createElement(ChevronLeft, { className: "text-slate-500" })}
          </button>

          {[...Array(totalPages)].slice(0, 5).map((_, index) => {
            const pageNumber = index + 1;
            return (
              <button
                key={pageNumber}
                className={`p-1 text-center text-sm font-medium px-3 rounded-lg border border-[#697b87]/0 hover:bg-gray-300 ${
                  currentPage === pageNumber ? "text-white bg-slate-500" : "text-slate-500 bg-slate-100"
                }`}
                onClick={() => setCurrentPage(pageNumber)}
              >
                {pageNumber}
              </button>
            );
          })}

          <button
            className="p-2 rounded-md bg-white hover:bg-gray-300 disabled:opacity-50"
            onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
          >
            {React.createElement(ChevronRight, { className: "text-slate-500" })}
          </button>
        </div>

        {/* Items per page */}
        <div className="flex items-center gap-3">
          <span className="text-slate-500 text-sm font-normal">Items per page</span>
          <CustomSelect value={String(pageSize)} options={["20", "40", "60"]} width="w-20" onChange={handlePageSizeChange} />
        </div>
      </div>
    </div>
  );
}
